import { CDN_PROD_HOST } from './config/cdn';
import { staticUrl } from './config/static-assets';
import { handleImgToWebp } from './config/image-webp';
import { shareAsync } from './share-async';

export type HomepageContentLink = {
  title: string;
  description: string;
  url: string;
  image: string;
};
export type HomepageGuideLink = HomepageContentLink;
export type HomepageGuideAlbum = {
  id: string;
  title: string;
  cover: string;
  postIds: string[];
};

/** OBS 直链规范化为生产图床（存储用）；展示再走 handleImgToWebp 做环境改写 + webp。 */
export function guideAlbumCoverCDNURL(value: string) {
  try {
    const url = new URL(value);
    if (url.hostname.includes('.obs.') && url.hostname.endsWith('.myhuaweicloud.com')) {
      url.protocol = 'https:';
      url.hostname = CDN_PROD_HOST;
      return url.toString();
    }
  } catch {
    // Local preview URLs and relative assets are left unchanged.
  }
  return value;
}

/** 专辑封面展示 URL：统一经 handleImgToWebp（本地 v2→test + webp）。 */
export function guideAlbumCoverDisplayURL(value: string) {
  return handleImgToWebp(guideAlbumCoverCDNURL(value));
}
export type HomepageNotice = {
  id: string;
  type: '情报' | '更新' | '公告';
  title: string;
  url: string;
};
export type HomepageBenefit = {
  id: string;
  date: string;
  updatedAt: string;
  expiresAt: string | null;
  label: string;
  code: string;
};
export type HomepagePromotion = {
  title: string;
  description: string;
  image: string;
  url: string;
};
export type HomepageNavigationSection = {
  id: string;
  title: string;
  layout: 'compact' | 'feature';
  showDescriptions: boolean;
  items: HomepageContentLink[];
};
export type HomepageConfig = {
  heroBackground: string;
  launchDate: string;
  toolsEnabled: boolean;
  benefitsEnabled: boolean;
  homeSidebarModule: 'benefits' | 'communityHot';
  versionTitle: string;
  featuredGuide: {
    title: string;
    image: string;
    url: string;
  };
  guideLinks: HomepageGuideLink[];
  guideRecommendations: HomepageGuideLink[];
  guideCategories: string[];
  guideAlbums: HomepageGuideAlbum[];
  notices: HomepageNotice[];
  benefits: HomepageBenefit[];
  promotions: HomepagePromotion[];
  newcomerLinks: HomepageContentLink[];
  catalogLinks: HomepageContentLink[];
  toolLinks: HomepageContentLink[];
  toolCards: HomepageContentLink[];
  navigationSections: HomepageNavigationSection[];
  /** @deprecated Kept while older saved homepage configurations migrate. */
  customNavigationSections: HomepageNavigationSection[];
};

/** @deprecated Prefer homepageConfigStorageKey(gameKey) */
export const HOMEPAGE_CONFIG_STORAGE_KEY = 'game-wiki-homepage-config-v1';

export function homepageConfigStorageKey(gameKey: string) {
  return `game-wiki-homepage-config-v1:${gameKey}`;
}

const homepageConfigTemplate: HomepageConfig = {
  heroBackground: '/assets/game-wiki/wiki-hero.png',
  launchDate: '2023-06-22',
  toolsEnabled: true,
  benefitsEnabled: false,
  homeSidebarModule: 'communityHot',
  versionTitle: '版本攻略',
  featuredGuide: {
    title: '本期魔兽（风）攻略合集',
    image: '/legacy-game-wiki/assets/sidebar-ad-placeholder-wide.png',
    url: '/arcanatactics/guide?title=本期魔兽（风）攻略合集&type=副本攻略',
  },
  guideLinks: [
    {
      title: '本期魔兽通关攻略',
      description: '机制、阵容与通关要点',
      url: '/arcanatactics/guide?title=本期魔兽通关攻略&type=副本攻略',
      image: '/legacy-game-wiki/assets/sidebar-ad-placeholder-wide.png',
    },
    {
      title: '黄金竞技场配队',
      description: '当前版本热门阵容',
      url: '/arcanatactics/guide?title=黄金竞技场配队&type=竞技场攻略',
      image: '/legacy-game-wiki/assets/home-tiles/catalog-heroes.jpg',
    },
    {
      title: '装备强化优先级',
      description: '资源投入与强化顺序',
      url: '/arcanatactics/guide?title=装备强化优先级&type=养成攻略',
      image: '/legacy-game-wiki/assets/home-tiles/catalog-equipment.jpg',
    },
    {
      title: '新手资源分配指南',
      description: '开荒阶段资源规划',
      url: '/arcanatactics/guide?title=新手资源分配指南&type=新手攻略',
      image: '/legacy-game-wiki/assets/home-tiles/newcomer-resources.jpg',
    },
    {
      title: '高难副本阵容推荐',
      description: '高难内容配队思路',
      url: '/arcanatactics/guide?title=高难副本阵容推荐&type=副本攻略',
      image: '/legacy-game-wiki/assets/home-tiles/newcomer-plan.jpg',
    },
    {
      title: '活动商店兑换顺序',
      description: '限时商店性价比清单',
      url: '/arcanatactics/guide?title=活动商店兑换顺序&type=当前版本攻略',
      image: '/legacy-game-wiki/assets/home-tiles/catalog-rewards.jpg',
    },
  ],
  guideRecommendations: [
    {
      title: '开启冒险之前，先把方向找对',
      description: '开荒路线、每日目标与资源规划',
      url: '/arcanatactics/guide?title=开启冒险之前，先把方向找对&type=新手攻略',
      image: '/legacy-game-wiki/assets/home-tiles/newcomer-start.jpg',
    },
    {
      title: '资源有限，培养优先级怎么排？',
      description: '资源投入与长期培养规划',
      url: '/arcanatactics/guide?title=资源有限，培养优先级怎么排？&type=养成攻略',
      image: '/legacy-game-wiki/assets/home-tiles/newcomer-resources.jpg',
    },
    {
      title: '版本更新后，先看懂变化',
      description: '当前版本变化与阵容调整',
      url: '/arcanatactics/guide?title=版本更新后，先看懂变化&type=当前版本攻略',
      image: '/legacy-game-wiki/assets/home-tiles/catalog-heroes.jpg',
    },
    {
      title: '卡关之后，如何拆解 Boss 机制？',
      description: '副本机制、阶段与失败节点',
      url: '/arcanatactics/guide?title=卡关之后，如何拆解 Boss 机制？&type=副本攻略',
      image: '/legacy-game-wiki/assets/home-tiles/catalog-equipment.jpg',
    },
  ],
  guideCategories: ['全部', '新手攻略', '养成攻略', '当前版本攻略', '副本攻略', '后期攻略'],
  guideAlbums: [],
  notices: [
    {
      id: 'notice-1',
      type: '情报',
      title: '9月特别直播预告，最新活动内容与奖励情报',
      url: '/arcanatactics/719595',
    },
    {
      id: 'notice-2',
      type: '更新',
      title: '开发者笔记53 · 9月更新内容说明',
      url: '/arcanatactics',
    },
    {
      id: 'notice-3',
      type: '情报',
      title: '紧急直播内容汇总，分地区提供服务',
      url: '/arcanatactics/718927',
    },
  ],
  benefits: [
    {
      id: 'benefit-1',
      date: '2026-09-07',
      updatedAt: '2026-09-07T00:00:00.000Z',
      expiresAt: null,
      label: '1 个武器自选券（演示）',
      code: 'DEMO-WEAPON-SELECT',
    },
    {
      id: 'benefit-2',
      date: '2026-09-07',
      updatedAt: '2026-09-07T00:00:00.000Z',
      expiresAt: null,
      label: '1 个角色自选券（演示）',
      code: 'DEMO-HERO-SELECT',
    },
  ],
  promotions: [
    {
      title: '新手补给指南',
      description: '入门资源速查',
      image: '',
      url: '/arcanatactics/guide?title=新手资源分配指南&type=新手攻略',
    },
    {
      title: '本期阵容推荐',
      description: '查看热门搭配',
      image: '',
      url: '/arcanatactics/guide?title=黄金竞技场配队&type=竞技场攻略',
    },
  ],
  newcomerLinks: [
    {
      title: '下载与登录',
      description: '安装游戏 · 账号准备',
      url: '/arcanatactics/guide?title=下载与登录&type=新手攻略',
      image: '/legacy-game-wiki/assets/home-tiles/newcomer-login.jpg',
    },
    {
      title: '新手上路必看',
      description: '基础机制 · 首发阵容',
      url: '/arcanatactics/guide?title=新手上路必看&type=新手攻略',
      image: '/legacy-game-wiki/assets/home-tiles/newcomer-start.jpg',
    },
    {
      title: '资源获取指南',
      description: '日常收益 · 活动奖励',
      url: '/arcanatactics/guide?title=资源获取指南&type=新手攻略',
      image: '/legacy-game-wiki/assets/home-tiles/newcomer-resources.jpg',
    },
    {
      title: '资源投入规划',
      description: '培养顺序 · 少走弯路',
      url: '/arcanatactics/guide?title=资源投入规划&type=新手攻略',
      image: '/legacy-game-wiki/assets/home-tiles/newcomer-plan.jpg',
    },
  ],
  catalogLinks: [
    {
      title: '英雄图鉴',
      description: '角色 · 属性 · 职业',
      url: '/arcanatactics/dcatalog',
      image: '/legacy-game-wiki/assets/home-tiles/catalog-heroes.jpg',
    },
    {
      title: '卡牌图鉴',
      description: '卡牌 · 效果 · 搭配',
      url: '/arcanatactics/dcatalog',
      image: '/legacy-game-wiki/assets/home-tiles/catalog-cards.jpg',
    },
    {
      title: '装备图鉴',
      description: '武器 · 护具 · 词条',
      url: '/arcanatactics/dcatalog',
      image: '/legacy-game-wiki/assets/home-tiles/catalog-equipment.jpg',
    },
    {
      title: '奖励图鉴',
      description: '道具 · 奖励 · 来源',
      url: '/arcanatactics/dcatalog',
      image: '/legacy-game-wiki/assets/home-tiles/catalog-rewards.jpg',
    },
  ],
  toolLinks: [
    {
      title: '礼包码自动兑换器',
      description: '批量识别礼包码，一键完成兑换',
      url: '/arcanatactics/dtools',
      image: '',
    },
    {
      title: '阵容搭配器',
      description: '组合英雄与站位，快速规划队伍',
      url: '/arcanatactics/dtools',
      image: '',
    },
    {
      title: '英雄练度打分器',
      description: '分析养成数据，评估英雄练度',
      url: '/arcanatactics/dtools',
      image: '',
    },
  ],
  toolCards: [
    {
      title: '阵容编辑器',
      description: '选择角色、安排站位，保存并分享你的阵容思路。',
      url: '',
      image: '',
    },
    {
      title: '装备搭配器',
      description: '组合装备与套装，比较属性变化和不同搭配方案。',
      url: '',
      image: '',
    },
    {
      title: '伤害计算器',
      description: '根据角色、技能与目标条件，预估伤害并对比配置。',
      url: '',
      image: '',
    },
    {
      title: '抽卡模拟器',
      description: '模拟抽取过程，了解次数与资源消耗的变化。',
      url: '',
      image: '',
    },
    {
      title: '数据统计打分',
      description: '汇总角色与装备数据，按明确的评分规则进行比较。',
      url: '',
      image: '',
    },
    {
      title: '礼包码自动兑换器',
      description: '整理礼包码、检查使用状态，统一查看兑换结果。',
      url: '',
      image: '',
    },
  ],
  navigationSections: [],
  customNavigationSections: [],
};

export function createDefaultHomepageConfig(gameKey: string): HomepageConfig {
  const cloned = JSON.parse(
    JSON.stringify(homepageConfigTemplate),
  ) as HomepageConfig;
  cloned.navigationSections = [
    { id: 'newcomer', title: '新人专区', layout: 'compact', showDescriptions: true, items: cloned.newcomerLinks },
    { id: 'catalog', title: '游戏资料库', layout: 'compact', showDescriptions: true, items: cloned.catalogLinks },
    { id: 'tools', title: '游戏工具', layout: 'feature', showDescriptions: true, items: cloned.toolLinks },
  ];
  if (gameKey === 'arcanatactics') return cloned;
  cloned.toolsEnabled = false;
  cloned.benefitsEnabled = false;
  return JSON.parse(
    JSON.stringify(cloned).replaceAll('/arcanatactics', `/${gameKey}`),
  ) as HomepageConfig;
}

/**
 * 把首页配置里的站内路径 `/{gameKey}/...` 改成 `/{domain}/...`，
 * 不改 CDN 资源路径（如 `.../wiki4.0/{gameKey}/image/...`）。
 */
export function rewriteHomepageSitePaths(
  config: HomepageConfig,
  gameKey: string,
  gameDomain: string,
): HomepageConfig {
  if (!gameKey || !gameDomain || gameKey === gameDomain) return config;
  const raw = JSON.stringify(config);
  const next = raw
    .replaceAll(`"/${gameKey}/`, `"/${gameDomain}/`)
    .replaceAll(`"/${gameKey}"`, `"/${gameDomain}"`)
    .replaceAll(`"/${gameKey}?`, `"/${gameDomain}?`);
  return JSON.parse(next) as HomepageConfig;
}

export const defaultHomepageConfig: HomepageConfig = applyStaticAssetUrls(
  createDefaultHomepageConfig('arcanatactics'),
);

export function cloneHomepageConfig(
  config: HomepageConfig = defaultHomepageConfig,
) {
  return JSON.parse(JSON.stringify(config)) as HomepageConfig;
}

function normalizeHomepageUrl(url: string, gameKey: string) {
  return url.replace(/^\/undefined(?=\/|$)/, `/${gameKey}`);
}

/** 壳内演示用本地 tile，不应作为线上展示地址（需后台上传 CDN）。 */
export function isLegacyHomeTileImage(value: string) {
  return /(?:^|\/)legacy-game-wiki\/assets\/home-tiles\//i.test(
    (value || '').trim(),
  );
}

/** 首页可展示图：丢掉本地 home-tiles，其余走 webp/CDN 改写。 */
export function homepageDisplayImage(value: string) {
  const image = (value || '').trim();
  if (!image || isLegacyHomeTileImage(image)) return '';
  return handleImgToWebp(image);
}

function mapStaticAssetLink<T extends { image?: string; cover?: string }>(item: T): T {
  return {
    ...item,
    ...(typeof item.image === 'string' ? { image: staticUrl(item.image) } : {}),
    ...(typeof item.cover === 'string' ? { cover: staticUrl(item.cover) } : {}),
  };
}

function applyStaticAssetUrls(config: HomepageConfig): HomepageConfig {
  return {
    ...config,
    heroBackground: staticUrl(config.heroBackground),
    featuredGuide: mapStaticAssetLink(config.featuredGuide),
    guideLinks: config.guideLinks.map(mapStaticAssetLink),
    guideRecommendations: config.guideRecommendations.map(mapStaticAssetLink),
    guideAlbums: config.guideAlbums.map(mapStaticAssetLink),
    promotions: config.promotions.map(mapStaticAssetLink),
    newcomerLinks: config.newcomerLinks.map(mapStaticAssetLink),
    catalogLinks: config.catalogLinks.map(mapStaticAssetLink),
    toolLinks: config.toolLinks.map(mapStaticAssetLink),
    toolCards: config.toolCards.map(mapStaticAssetLink),
    navigationSections: config.navigationSections.map((section) => ({
      ...section,
      items: section.items.map(mapStaticAssetLink),
    })),
    customNavigationSections: config.customNavigationSections.map((section) => ({
      ...section,
      items: section.items.map(mapStaticAssetLink),
    })),
  };
}

function normalizeHomepageLinks<T extends { url: string }>(
  links: T[],
  gameKey: string,
) {
  return links.map((link) => ({
    ...link,
    url: normalizeHomepageUrl(link.url, gameKey),
  }));
}

export function normalizeHomepageConfig(
  value: unknown,
  gameKey = 'arcanatactics',
): HomepageConfig {
  const stored =
    value && typeof value === 'object'
      ? (value as Partial<HomepageConfig>)
      : {};
  const fallback = createDefaultHomepageConfig(gameKey);
  const guideLinks = Array.isArray(stored.guideLinks)
    ? stored.guideLinks.slice(0, 6).map((guide, index) => ({
        title: typeof guide.title === 'string' ? guide.title : '',
        description:
          typeof guide.description === 'string' ? guide.description : '',
        url: typeof guide.url === 'string' ? guide.url : '',
        image:
          typeof guide.image === 'string'
            ? guide.image
            : index === 0
              ? stored.featuredGuide?.image || ''
              : '',
      }))
    : [];
  const guideCategories = Array.isArray(stored.guideCategories)
    ? stored.guideCategories
        .filter((category): category is string => typeof category === 'string')
        .map((category) => category.trim())
        .filter(Boolean)
        .slice(0, 12)
    : [];
  const newcomerLinks = normalizeHomepageLinks(
    Array.isArray(stored.newcomerLinks)
      ? stored.newcomerLinks.slice(0, 4)
      : fallback.newcomerLinks,
    gameKey,
  );
  const catalogLinks = normalizeHomepageLinks(
    Array.isArray(stored.catalogLinks)
      ? stored.catalogLinks.slice(0, 4)
      : fallback.catalogLinks,
    gameKey,
  );
  const toolLinks = normalizeHomepageLinks(
    Array.isArray(stored.toolLinks)
      ? stored.toolLinks.slice(0, 3)
      : fallback.toolLinks,
    gameKey,
  ).map((link) => ({
    ...link,
    image: /^\/legacy-game-wiki\/assets\/home-tiles\/(catalog-rewards|catalog-heroes|catalog-equipment)\.jpg$/.test(link.image)
      ? ''
      : link.image,
  }));
  const legacyCustomSections = Array.isArray(stored.customNavigationSections)
    ? stored.customNavigationSections.slice(0, 8).map((section, index) => ({
        id: section.id || `custom-navigation-${index + 1}`,
        title: section.title || `更多导航 ${index + 1}`,
        layout: section.layout === 'feature' ? 'feature' as const : 'compact' as const,
        showDescriptions: section.showDescriptions !== false,
        items: normalizeHomepageLinks(
          Array.isArray(section.items) ? section.items.slice(0, 6) : [],
          gameKey,
        ),
      }))
    : [];
  const navigationSections: HomepageNavigationSection[] = Array.isArray(stored.navigationSections)
    ? stored.navigationSections.slice(0, 10).map((section, index) => ({
        id: section.id || `navigation-${index + 1}`,
        title: section.title || `导航模块 ${index + 1}`,
        layout: section.layout === 'feature' ? 'feature' : 'compact',
        showDescriptions: section.showDescriptions !== false,
        items: normalizeHomepageLinks(
          Array.isArray(section.items) ? section.items.slice(0, 6) : [],
          gameKey,
        ),
      }))
    : [
        { id: 'newcomer', title: '新人专区', layout: 'compact', showDescriptions: true, items: newcomerLinks },
        { id: 'catalog', title: '游戏资料库', layout: 'compact', showDescriptions: true, items: catalogLinks },
        { id: 'tools', title: '游戏工具', layout: 'feature', showDescriptions: true, items: toolLinks },
        ...legacyCustomSections,
      ];
  const normalized: HomepageConfig = {
    ...cloneHomepageConfig(fallback),
    ...stored,
    toolsEnabled:
      typeof stored.toolsEnabled === 'boolean'
        ? stored.toolsEnabled
        : fallback.toolsEnabled,
    benefitsEnabled:
      typeof stored.benefitsEnabled === 'boolean'
        ? stored.benefitsEnabled
        : fallback.benefitsEnabled,
    homeSidebarModule:
      stored.homeSidebarModule === 'benefits' ||
      stored.homeSidebarModule === 'communityHot'
        ? stored.homeSidebarModule
        : stored.benefitsEnabled
          ? 'benefits'
          : 'communityHot',
    featuredGuide: {
      ...fallback.featuredGuide,
      ...stored.featuredGuide,
      url: normalizeHomepageUrl(
        stored.featuredGuide?.url || fallback.featuredGuide.url,
        gameKey,
      ),
    },
    guideLinks: normalizeHomepageLinks(guideLinks, gameKey),
    guideRecommendations: normalizeHomepageLinks(
      Array.isArray(stored.guideRecommendations)
        ? stored.guideRecommendations.slice(0, 8)
        : cloneHomepageConfig().guideRecommendations,
      gameKey,
    ),
    guideCategories: guideCategories.length
      ? guideCategories
      : cloneHomepageConfig().guideCategories,
    guideAlbums: Array.isArray(stored.guideAlbums)
      ? stored.guideAlbums.slice(0, 12).map((album, index) => ({
          id: album.id || `guide-album-${index + 1}`,
          title: typeof album.title === 'string' ? album.title.trim() : '',
          cover: typeof album.cover === 'string' ? album.cover : '',
          postIds: Array.isArray(album.postIds)
            ? album.postIds
                .filter((postId): postId is string => typeof postId === 'string')
                .slice(0, 30)
            : [],
        })).filter((album) => album.title && album.postIds.length)
      : [],
    notices: normalizeHomepageLinks(
      Array.isArray(stored.notices)
        ? stored.notices
        : cloneHomepageConfig().notices,
      gameKey,
    ),
    benefits: (Array.isArray(stored.benefits)
      ? stored.benefits
      : fallback.benefits
    ).map((benefit, index) => {
      const legacyDate =
        typeof benefit.date === 'string' && benefit.date
          ? benefit.date
          : fallback.benefits[index]?.date || '2026-01-01';
      const updatedAt =
        typeof benefit.updatedAt === 'string' && benefit.updatedAt
          ? benefit.updatedAt
          : `${legacyDate}T00:00:00.000Z`;
      return {
        id: benefit.id || `benefit-${index + 1}`,
        date: legacyDate,
        updatedAt,
        expiresAt:
          typeof benefit.expiresAt === 'string' && benefit.expiresAt
            ? benefit.expiresAt
            : null,
        label: benefit.label || '',
        code: benefit.code || '',
      };
    }),
    promotions: normalizeHomepageLinks(
      Array.isArray(stored.promotions)
        ? stored.promotions.slice(0, 2)
        : fallback.promotions,
      gameKey,
    ).map((link) => ({
      ...link,
      image: isLegacyHomeTileImage(link.image) ? '' : link.image,
    })),
    newcomerLinks,
    catalogLinks,
    toolLinks,
    toolCards: normalizeHomepageLinks(
      Array.isArray(stored.toolCards)
        ? stored.toolCards.slice(0, 6)
        : fallback.toolCards,
      gameKey,
    ),
    navigationSections,
    customNavigationSections: legacyCustomSections,
  };
  return applyStaticAssetUrls(normalized);
}

export function readHomepageConfig(gameKey: string) {
  try {
    let raw = localStorage.getItem(homepageConfigStorageKey(gameKey));
    // Migrate unscoped arcanatactics config written before multi-game keys.
    if (!raw && gameKey === 'arcanatactics') {
      raw = localStorage.getItem(HOMEPAGE_CONFIG_STORAGE_KEY);
    }
    const stored = JSON.parse(raw || 'null') as Partial<HomepageConfig> | null;
    if (!stored) return applyStaticAssetUrls(createDefaultHomepageConfig(gameKey));
    return normalizeHomepageConfig(stored, gameKey);
  } catch {
    return applyStaticAssetUrls(createDefaultHomepageConfig(gameKey));
  }
}

export function saveHomepageConfig(gameKey: string, config: HomepageConfig) {
  localStorage.setItem(homepageConfigStorageKey(gameKey), JSON.stringify(config));
}

const homepageConfigInflight = new Map<string, Promise<HomepageConfig>>();

export async function fetchHomepageConfig(gameKey: string) {
  return shareAsync(homepageConfigInflight, gameKey, async () => {
    const response = await fetch(`/api/homepage/${encodeURIComponent(gameKey)}`, {
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('首页配置读取失败');
    const payload = (await response.json()) as { config?: unknown };
    return normalizeHomepageConfig(payload.config, gameKey);
  });
}

export async function updateHomepageConfig(
  gameKey: string,
  config: HomepageConfig,
  token: string,
) {
  const response = await fetch(`/api/homepage/${encodeURIComponent(gameKey)}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json', 'access-token': token },
    body: JSON.stringify({ config }),
  });
  const payload = (await response.json().catch(() => ({}))) as {
    config?: unknown;
    error?: string;
  };
  if (!response.ok) throw new Error(payload.error || '首页配置保存失败');
  return normalizeHomepageConfig(payload.config, gameKey);
}

/** 管理后台图片上传：浏览器直传 OBS，返回 CDN URL。token 仅保留兼容调用方校验登录。 */
export async function uploadHomepageImage(
  gameKey: string,
  file: File,
  _token?: string,
) {
  const { uploadToObs } = await import('./obs/client-upload');
  return uploadToObs({
    file,
    gameDomain: gameKey,
    fileType: 'image',
  });
}

export type WikiAdministrator = {
  uid: string;
  username: string;
  avatar: string;
  addedBy?: string;
  createdAt?: string;
};

export type WikiGameProfile = {
  key: string;
  name: string;
  icon: string;
  domain: string;
};

export async function updateWikiGameProfile(
  gameKey: string,
  profile: Pick<WikiGameProfile, 'name' | 'icon' | 'domain'>,
  token: string,
) {
  const response = await fetch(
    `/api/games/${encodeURIComponent(gameKey)}/profile`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json', 'access-token': token },
      body: JSON.stringify(profile),
    },
  );
  const payload = (await response.json().catch(() => ({}))) as {
    game?: WikiGameProfile;
    error?: string;
  };
  if (!response.ok || !payload.game) {
    throw new Error(payload.error || '游戏信息保存失败');
  }
  return payload.game;
}

async function administratorRequest<T>(
  gameKey: string,
  token: string,
  path = '',
  init?: RequestInit,
) {
  const response = await fetch(
    `/api/games/${encodeURIComponent(gameKey)}/admins${path}`,
    {
      ...init,
      cache: 'no-store',
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        'access-token': token,
        ...init?.headers,
      },
    },
  );
  const payload = (await response.json().catch(() => ({}))) as T & {
    error?: string;
  };
  if (!response.ok) throw new Error(payload.error || '管理员操作失败');
  return payload;
}

export async function fetchWikiAdministrators(gameKey: string, token: string) {
  const payload = await administratorRequest<{ admins?: WikiAdministrator[] }>(
    gameKey,
    token,
  );
  return payload.admins || [];
}

export async function lookupWikiAdministrator(
  gameKey: string,
  uid: string,
  token: string,
) {
  const payload = await administratorRequest<{ user: WikiAdministrator }>(
    gameKey,
    token,
    `/lookup?uid=${encodeURIComponent(uid)}`,
  );
  return payload.user;
}

export async function addWikiAdministrator(
  gameKey: string,
  uid: string,
  token: string,
) {
  const payload = await administratorRequest<{ admin: WikiAdministrator }>(
    gameKey,
    token,
    '',
    { method: 'POST', body: JSON.stringify({ uid }) },
  );
  return payload.admin;
}
